import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  AVERAGE_MINUTES_PER_CALL,
  PLAN_TIERS,
  estimatePlanTierByUsage,
  getPlanTier,
  recommendPlanTierByDoors,
  type PlanTier,
  type PlanTierId,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep } from '../flow.config'
import { PlanPickerPanel } from '../components/PlanPickerPanel'
import { CONTACT_SALES_MAILTO } from '../sales'

const CALLS_MIN = 0
const CALLS_MAX = 2200
const CALLS_STEP = 1
const SMS_MIN = 0
const SMS_MAX = 2000
const SMS_STEP = 5

export function PlanStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { state } = useOnboarding()

  const doorSeededTier = useMemo(
    () => recommendPlanTierByDoors(state.doors ?? 0),
    [state.doors],
  )

  const seedTierParam = searchParams.get('tier')
  // Fall back to the door-based recommendation on an unknown/garbage tier
  // param so a malformed URL doesn't crash the render.
  const seedTier =
    PLAN_TIERS.find(t => t.id === seedTierParam) ?? doorSeededTier

  // Source of truth: minutes + SMS. Best-fit derives from those, and the
  // best-fit tier IS the selection — there is no separate "selected" state
  // that could drift from what the estimator is recommending.
  const [minutesEstimate, setMinutesEstimate] = useState<number>(
    seedTier.includedMinutes,
  )
  const [smsPerMonth, setSmsPerMonth] = useState<number>(seedTier.includedSms)

  const callsPerMonth = Math.round(minutesEstimate / AVERAGE_MINUTES_PER_CALL)

  const bestFit = useMemo(
    () =>
      estimatePlanTierByUsage({
        minutesEstimate,
        smsEstimate: smsPerMonth,
      }),
    [minutesEstimate, smsPerMonth],
  )

  const tierBeingProceededWith = bestFit?.tier ?? null
  const highlightedPickerId = bestFit?.tier.id ?? 'contact-sales'

  const handleCallsChange = (value: number) => {
    setMinutesEstimate(value * AVERAGE_MINUTES_PER_CALL)
  }

  const handleSelectTier = (tierId: PlanTierId) => {
    const tier = getPlanTier(tierId)
    setMinutesEstimate(tier.includedMinutes)
    setSmsPerMonth(tier.includedSms)
  }

  const advanceToNextStep = () => {
    if (!tierBeingProceededWith) {
      window.location.href = CONTACT_SALES_MAILTO
      return
    }
    const current = findStepByRoute(location.pathname)
    const next = current ? nextStep(current, state.userType ?? null) : null
    if (!next) return
    navigate(`${next.route}?tier=${tierBeingProceededWith.id}`)
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <div className="space-y-1 text-center">
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · PLAN
        </p>
        <h1 className="font-space-grotesk text-2xl font-semibold tracking-tight">
          Estimate your usage
        </h1>
      </div>

      <section className="border-border bg-muted/30 flex flex-col gap-4 rounded-lg border p-4">
        <div className="grid gap-4 md:grid-cols-2">
          <SliderRow
            label="Estimated calls per month"
            value={callsPerMonth}
            min={CALLS_MIN}
            max={CALLS_MAX}
            step={CALLS_STEP}
            onChange={handleCallsChange}
            hint={`~${AVERAGE_MINUTES_PER_CALL} min/call → ${Math.round(minutesEstimate).toLocaleString()} min`}
          />
          <SliderRow
            label="Estimated SMS per month"
            value={smsPerMonth}
            min={SMS_MIN}
            max={SMS_MAX}
            step={SMS_STEP}
            onChange={setSmsPerMonth}
          />
        </div>

        <RecommendationLabel
          bestFitTier={bestFit?.tier ?? null}
          bestFitMonthlyCost={bestFit?.monthlyCost ?? null}
        />
      </section>

      <PlanPickerPanel
        selectedTierId={highlightedPickerId}
        recommendedTierId={highlightedPickerId}
        onSelect={handleSelectTier}
      />

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-plan"
        priority={10}
      >
        <div className="pointer-events-auto flex items-center gap-3">
          <span className="text-muted-foreground hidden text-xs sm:inline">
            Stress-free changing of plans your first month
          </span>
          <Button onClick={advanceToNextStep} size="sm">
            {tierBeingProceededWith
              ? `Select ${tierBeingProceededWith.label} plan →`
              : 'Contact sales →'}
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}

function SliderRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
  hint,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (n: number) => void
  hint?: string
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-medium">{label}</label>
        <span className="font-space-grotesk text-base font-semibold tracking-tight">
          {value.toLocaleString()}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={values => onChange(values[0] ?? 0)}
      />
      {hint ? (
        <p className="text-muted-foreground text-[11px]">{hint}</p>
      ) : null}
    </div>
  )
}

function RecommendationLabel({
  bestFitTier,
  bestFitMonthlyCost,
}: {
  bestFitTier: PlanTier | null
  bestFitMonthlyCost: number | null
}) {
  const bestFitTitle = bestFitTier
    ? `Estimated best value: ${bestFitTier.label} plan`
    : 'Estimated best value: contact sales for a custom bundle'
  const bestFitCost =
    bestFitMonthlyCost !== null
      ? `Estimated monthly total cost: $${Math.round(bestFitMonthlyCost).toLocaleString()}/mo`
      : null
  const bestFitKey = bestFitTier?.id ?? 'contact-sales'

  return (
    <div
      className="border-border/60 bg-background flex flex-col gap-0.5 rounded-md border px-3 py-2"
      aria-live="polite"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={bestFitKey}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="flex flex-col gap-0.5"
        >
          <span className="text-foreground text-sm font-medium">
            {bestFitTitle}
          </span>
          {bestFitCost ? (
            <span className="text-muted-foreground text-[11px]">
              {bestFitCost}
            </span>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
